import React, { useState, useEffect } from 'react';

// Convert any date-ish value (ISO string, Date, "YYYY-MM-DD") to the
// "YYYY-MM-DD" string that <input type="date"> expects. Returns '' if invalid.
const toDateInputValue = (value) => {
    if (!value) return '';
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return '';
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    } catch (e) {
        return '';
    }
};

const resolveCategoryId = (categoryValue, categories) => {
    if (!categoryValue) return '';
    const raw = typeof categoryValue === 'object'
        ? (categoryValue.id || categoryValue._id || '')
        : String(categoryValue);
    if (!raw) return '';
    const byId = categories.find(c => c.id === raw || c._id === raw);
    if (byId) return byId.id || byId._id;
    const byName = categories.find(c => c.name === raw);
    if (byName) return byName.id || byName._id;
    return raw;
};

const emptyForm = {
    title: '',
    destination: '',
    country: '',
    category: '',
    price: '',
    days: '',
    nights: '',
    rating: 0,
    description: '',
    isActive: true,
    isFeatured: false,
    highlights: [''],
    itinerary: [],
    inclusions: [''],
    exclusions: [''],
    images: [],
    tags: '',
    availableDates: []
};

const TourModal = ({ show, onClose, tour, loadingTour = false, categories, onSave }) => {
    const [currentTab, setCurrentTab] = useState('basic');
    const [uploadingImages, setUploadingImages] = useState(false);
    const [errors, setErrors] = useState({});
    const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');
    const [formData, setFormData] = useState(emptyForm);

    useEffect(() => {
        if (!show) return;

        if (loadingTour) {
            return;
        }

        if (tour) {
            setFormData({
                title: tour.title || '',
                destination: tour.destination || '',
                country: tour.country || '',
                category: resolveCategoryId(tour.category, categories),
                price: tour.price ?? '',
                days: tour.duration?.days ?? '',
                nights: tour.duration?.nights ?? '',
                rating: tour.rating ?? 0,
                description: tour.description || '',
                isActive: tour.isActive !== undefined ? tour.isActive : true,
                isFeatured: tour.isFeatured || false,
                highlights: tour.highlights?.length > 0 ? [...tour.highlights] : [''],
                itinerary: (tour.itinerary || []).map(it => ({
                    day: it.day,
                    title: it.title || '',
                    description: it.description || '',
                    activities: it.activities || []
                })),
                inclusions: tour.inclusions?.length > 0 ? [...tour.inclusions] : [''],
                exclusions: tour.exclusions?.length > 0 ? [...tour.exclusions] : [''],
                images: tour.images || [],
                tags: tour.tags?.join(', ') || '',
                availableDates: (tour.availableDates || []).map(d => ({
                    startDate: toDateInputValue(d.startDate),
                    endDate: toDateInputValue(d.endDate),
                    spotsAvailable: d.spotsAvailable ?? 0
                }))
            });
        } else {
            setFormData(emptyForm);
        }
        setErrors({});
        setCurrentTab('basic');
    }, [tour, show, loadingTour, categories]);

    // Validate all required fields across all tabs and return the first invalid tab + a map of errors.
    const validate = () => {
        const errs = {};
        if (!formData.title.trim()) errs.title = 'Title is required';
        if (!formData.destination.trim()) errs.destination = 'Destination is required';
        if (!formData.country.trim()) errs.country = 'Country is required';
        if (!formData.category) errs.category = 'Category is required';
        if (formData.price === '' || isNaN(parseFloat(formData.price))) errs.price = 'Valid price is required';
        if (formData.days === '' || isNaN(parseInt(formData.days, 10))) errs.days = 'Days is required';
        if (formData.nights === '' || isNaN(parseInt(formData.nights, 10))) errs.nights = 'Nights is required';
        if (!formData.description.trim()) errs.description = 'Description is required';

        // Determine which tab the first error lives on so we can switch to it
        const basicKeys = ['title', 'destination', 'country', 'category', 'price', 'days', 'nights'];
        const detailsKeys = ['description'];
        let firstErrorTab = null;
        if (basicKeys.some(k => errs[k])) firstErrorTab = 'basic';
        else if (detailsKeys.some(k => errs[k])) firstErrorTab = 'details';

        return { errs, firstErrorTab };
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const { errs, firstErrorTab } = validate();
        setErrors(errs);
        if (Object.keys(errs).length > 0) {
            if (firstErrorTab) setCurrentTab(firstErrorTab);
            alert(`Please fix the following before saving:\n• ${Object.values(errs).join('\n• ')}`);
            return;
        }

        const tourData = {
            title: formData.title.trim(),
            destination: formData.destination.trim(),
            country: formData.country.trim().toLowerCase(),
            category: formData.category,
            price: parseFloat(formData.price),
            duration: {
                days: parseInt(formData.days, 10),
                nights: parseInt(formData.nights, 10)
            },
            rating: parseFloat(formData.rating) || 0,
            description: formData.description.trim(),
            highlights: formData.highlights.filter(item => (item || '').trim()),
            itinerary: formData.itinerary
                .filter(it => it && (it.title || it.description))
                .map((it, idx) => ({
                    day: Number.isFinite(parseInt(it.day, 10)) ? parseInt(it.day, 10) : idx + 1,
                    title: it.title || '',
                    description: it.description || '',
                    activities: it.activities || []
                })),
            inclusions: formData.inclusions.filter(item => (item || '').trim()),
            exclusions: formData.exclusions.filter(item => (item || '').trim()),
            images: formData.images,
            tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
            availableDates: formData.availableDates
                .filter(d => d.startDate && d.endDate)
                .map(d => ({
                    startDate: new Date(d.startDate).toISOString(),
                    endDate: new Date(d.endDate).toISOString(),
                    spotsAvailable: parseInt(d.spotsAvailable, 10) || 0
                })),
            isActive: formData.isActive,
            isFeatured: formData.isFeatured
        };

        onSave(tourData);
    };

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setErrors(prev => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const addArrayItem = (field, defaultValue = '') => {
        setFormData(prev => ({
            ...prev,
            [field]: [...prev[field], defaultValue]
        }));
    };

    const updateArrayItem = (field, index, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].map((item, i) => i === index ? value : item)
        }));
    };

    const removeArrayItem = (field, index) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index)
        }));
    };

    const addItinerary = () => {
        addArrayItem('itinerary', { day: formData.itinerary.length + 1, title: '', description: '' });
    };

    const updateItinerary = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            itinerary: prev.itinerary.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        }));
    };

    const addAvailableDate = () => {
        addArrayItem('availableDates', { startDate: '', endDate: '', spotsAvailable: 0 });
    };

    const updateAvailableDate = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            availableDates: prev.availableDates.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        }));
    };

    const handleTourImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setUploadingImages(true);
        try {
            const uploaded = await Promise.all(files.map(async (file) => {
                const fd = new FormData();
                fd.append('file', file);
                const res = await fetch(`${API_URL}/api/upload?subfolder=tours`, {
                    method: 'POST',
                    body: fd
                });
                if (!res.ok) throw new Error('Upload failed');
                const data = await res.json();
                return data.url;
            }));
            setFormData(prev => ({ ...prev, images: [...prev.images, ...uploaded] }));
        } catch (err) {
            alert('Image upload failed: ' + err.message);
        } finally {
            setUploadingImages(false);
            e.target.value = '';
        }
    };

    if (!show) return null;

    if (loadingTour) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
                    <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
                    <p className="text-gray-700 font-medium">Loading tour details...</p>
                    <button
                        type="button"
                        onClick={onClose}
                        className="mt-6 text-gray-500 hover:text-gray-700"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    const tabHasError = (tabId) => {
        if (tabId === 'basic') {
            return ['title', 'destination', 'country', 'category', 'price', 'days', 'nights']
                .some(k => errors[k]);
        }
        if (tabId === 'details') return Boolean(errors.description);
        return false;
    };

    const tabs = [
        { id: 'basic', label: 'Basic Info' },
        { id: 'details', label: 'Details' },
        { id: 'itinerary', label: 'Itinerary' },
        { id: 'inclusions', label: 'Inclusions' },
        { id: 'media', label: 'Media' }
    ];

    const inputCls = (field) =>
        `w-full px-4 py-2 border rounded-lg ${errors[field] ? 'border-red-500 bg-red-50' : ''}`;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-2xl font-bold">{tour ? 'Edit Tour' : 'Add New Tour'}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <i className="fas fa-times text-2xl"></i>
                    </button>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 mb-4">
                    <nav className="flex space-x-4">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setCurrentTab(tab.id)}
                                className={`px-4 py-2 border-b-2 ${currentTab === tab.id
                                    ? 'border-blue-600 text-blue-600 font-semibold'
                                    : 'border-transparent text-gray-600 hover:text-gray-800'
                                    }`}
                            >
                                {tab.label}
                                {tabHasError(tab.id) && (
                                    <span className="ml-2 inline-block w-2 h-2 rounded-full bg-red-500" title="This tab has errors"></span>
                                )}
                            </button>
                        ))}
                    </nav>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Basic Info Tab */}
                    {currentTab === 'basic' && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Title *</label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => updateField('title', e.target.value)}
                                        className={inputCls('title')}
                                    />
                                    {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title}</p>}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Destination *</label>
                                    <input
                                        type="text"
                                        value={formData.destination}
                                        onChange={(e) => updateField('destination', e.target.value)}
                                        className={inputCls('destination')}
                                    />
                                    {errors.destination && <p className="text-red-600 text-sm mt-1">{errors.destination}</p>}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Country *</label>
                                    <input
                                        type="text"
                                        value={formData.country}
                                        onChange={(e) => updateField('country', e.target.value)}
                                        className={inputCls('country')}
                                    />
                                    {errors.country && <p className="text-red-600 text-sm mt-1">{errors.country}</p>}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Category *</label>
                                    <select
                                        value={formData.category}
                                        onChange={(e) => updateField('category', e.target.value)}
                                        className={inputCls('category')}
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                    {errors.category && <p className="text-red-600 text-sm mt-1">{errors.category}</p>}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Price (INR) *</label>
                                    <input
                                        type="number"
                                        value={formData.price}
                                        onChange={(e) => updateField('price', e.target.value)}
                                        className={inputCls('price')}
                                    />
                                    {errors.price && <p className="text-red-600 text-sm mt-1">{errors.price}</p>}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Days *</label>
                                    <input
                                        type="number"
                                        value={formData.days}
                                        onChange={(e) => updateField('days', e.target.value)}
                                        className={inputCls('days')}
                                    />
                                    {errors.days && <p className="text-red-600 text-sm mt-1">{errors.days}</p>}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Nights *</label>
                                    <input
                                        type="number"
                                        value={formData.nights}
                                        onChange={(e) => updateField('nights', e.target.value)}
                                        className={inputCls('nights')}
                                    />
                                    {errors.nights && <p className="text-red-600 text-sm mt-1">{errors.nights}</p>}
                                </div>

                            </div>
                            <div className="flex items-center space-x-4">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={formData.isActive}
                                        onChange={(e) => updateField('isActive', e.target.checked)}
                                        className="mr-2"
                                    />
                                    <span>Active</span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={formData.isFeatured}
                                        onChange={(e) => updateField('isFeatured', e.target.checked)}
                                        className="mr-2"
                                    />
                                    <span>Featured</span>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Details Tab */}
                    {currentTab === 'details' && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">Description *</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => updateField('description', e.target.value)}
                                    rows="6"
                                    className={inputCls('description')}
                                    placeholder="Enter detailed tour description..."
                                />
                                {errors.description && <p className="text-red-600 text-sm mt-1">{errors.description}</p>}
                            </div>
                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">Highlights</label>
                                {formData.highlights.map((highlight, index) => (
                                    <div key={index} className="flex gap-2 mb-2">
                                        <input
                                            type="text"
                                            value={highlight}
                                            onChange={(e) => updateArrayItem('highlights', index, e.target.value)}
                                            className="flex-1 px-4 py-2 border rounded-lg"
                                            placeholder="Enter highlight"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeArrayItem('highlights', index)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => addArrayItem('highlights', '')}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                    <i className="fas fa-plus mr-1"></i>Add Highlight
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Itinerary Tab */}
                    {currentTab === 'itinerary' && (
                        <div className="space-y-4">
                            {formData.itinerary.map((item, index) => (
                                <div key={index} className="border rounded-lg p-4 bg-gray-50">
                                    <div className="grid grid-cols-3 gap-4 mb-2">
                                        <div>
                                            <label className="block text-sm text-gray-600 mb-1">Day</label>
                                            <input
                                                type="number"
                                                value={item.day}
                                                onChange={(e) => updateItinerary(index, 'day', parseInt(e.target.value))}
                                                className="w-full px-3 py-2 border rounded"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm text-gray-600 mb-1">Title</label>
                                            <input
                                                type="text"
                                                value={item.title}
                                                onChange={(e) => updateItinerary(index, 'title', e.target.value)}
                                                className="w-full px-3 py-2 border rounded"
                                            />
                                        </div>
                                        <div className="flex items-end">
                                            <button
                                                type="button"
                                                onClick={() => removeArrayItem('itinerary', index)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-600 mb-1">Description</label>
                                        <textarea
                                            value={item.description}
                                            onChange={(e) => updateItinerary(index, 'description', e.target.value)}
                                            rows="2"
                                            className="w-full px-3 py-2 border rounded"
                                        />
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addItinerary}
                                className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                                <i className="fas fa-plus mr-1"></i>Add Day
                            </button>
                        </div>
                    )}

                    {/* Inclusions Tab */}
                    {currentTab === 'inclusions' && (
                        <div className="space-y-6">
                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">Inclusions</label>
                                {formData.inclusions.map((inclusion, index) => (
                                    <div key={index} className="flex gap-2 mb-2">
                                        <input
                                            type="text"
                                            value={inclusion}
                                            onChange={(e) => updateArrayItem('inclusions', index, e.target.value)}
                                            className="flex-1 px-4 py-2 border rounded-lg"
                                            placeholder="What's included"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeArrayItem('inclusions', index)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => addArrayItem('inclusions', '')}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                    <i className="fas fa-plus mr-1"></i>Add Inclusion
                                </button>
                            </div>
                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">Exclusions</label>
                                {formData.exclusions.map((exclusion, index) => (
                                    <div key={index} className="flex gap-2 mb-2">
                                        <input
                                            type="text"
                                            value={exclusion}
                                            onChange={(e) => updateArrayItem('exclusions', index, e.target.value)}
                                            className="flex-1 px-4 py-2 border rounded-lg"
                                            placeholder="What's not included"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeArrayItem('exclusions', index)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => addArrayItem('exclusions', '')}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                    <i className="fas fa-plus mr-1"></i>Add Exclusion
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Media Tab */}
                    {currentTab === 'media' && (
                        <div className="space-y-4">
                            {/* Image Upload */}
                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">Tour Images</label>
                                <label className={`flex items-center justify-center w-full px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition ${uploadingImages ? 'border-gray-300 bg-gray-50 cursor-not-allowed' : 'border-blue-400 hover:bg-blue-50'}`}>
                                    <i className={`fas ${uploadingImages ? 'fa-spinner fa-spin' : 'fa-cloud-upload-alt'} mr-2 text-blue-500`}></i>
                                    <span className="text-gray-600">{uploadingImages ? 'Uploading...' : 'Click to upload images'}</span>
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleTourImageUpload}
                                        disabled={uploadingImages}
                                        className="hidden"
                                    />
                                </label>
                                <p className="text-xs text-gray-500 mt-1">Images are uploaded immediately to the server (JPG, PNG, WEBP)</p>
                            </div>

                            {/* Image Preview */}
                            {formData.images.length > 0 && (
                                <div className="grid grid-cols-3 gap-4">
                                    {formData.images.map((img, index) => (
                                        <div key={index} className="relative">
                                            <img
                                                src={img.startsWith('/') ? `${API_URL}${img}` : img}
                                                alt={`Tour ${index + 1}`}
                                                className="w-full h-32 object-cover rounded"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        images: prev.images.filter((_, i) => i !== index)
                                                    }));
                                                }}
                                                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                                            >
                                                <i className="fas fa-times text-xs"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">Tags</label>
                                <input
                                    type="text"
                                    value={formData.tags}
                                    onChange={(e) => updateField('tags', e.target.value)}
                                    className="w-full px-4 py-2 border rounded-lg"
                                    placeholder="e.g., bestseller, romantic, europe"
                                />
                                <p className="text-xs text-gray-500 mt-1">Separate tags with commas</p>
                            </div>

                            {/* Available Dates */}
                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">Available Dates</label>
                                {formData.availableDates.map((date, index) => (
                                    <div key={index} className="grid grid-cols-4 gap-3 mb-3 items-end border rounded-lg p-3 bg-gray-50">
                                        <div>
                                            <label className="block text-sm text-gray-600 mb-1">Start Date</label>
                                            <input
                                                type="date"
                                                value={date.startDate}
                                                onChange={(e) => updateAvailableDate(index, 'startDate', e.target.value)}
                                                className="w-full px-3 py-2 border rounded"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm text-gray-600 mb-1">End Date</label>
                                            <input
                                                type="date"
                                                value={date.endDate}
                                                onChange={(e) => updateAvailableDate(index, 'endDate', e.target.value)}
                                                className="w-full px-3 py-2 border rounded"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm text-gray-600 mb-1">Spots Available</label>
                                            <input
                                                type="number"
                                                value={date.spotsAvailable}
                                                onChange={(e) => updateAvailableDate(index, 'spotsAvailable', parseInt(e.target.value))}
                                                className="w-full px-3 py-2 border rounded"
                                                min="0"
                                            />
                                        </div>
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        availableDates: prev.availableDates.filter((_, i) => i !== index)
                                                    }));
                                                }}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <i className="fas fa-trash"></i> Remove
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={addAvailableDate}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                    <i className="fas fa-plus mr-1"></i>Add Available Date
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Form Actions */}
                    <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 border rounded-lg hover:bg-gray-100"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            <i className="fas fa-save mr-2"></i>Save Tour
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TourModal;
